import React, { Suspense } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import CssBaseline from '@material-ui/core/CssBaseline';
import { Router, Route, Switch, Redirect } from 'react-router-dom';
import { Provider } from 'react-redux';
import { createBrowserHistory } from 'history';
import { Home } from 'src/pages/Home/Home';
import { CoreLoader } from '@components/CoreLoader/CoreLoader';
import { ThemeProvider } from '@material-ui/core';
import { theme } from '@theme/customTheme';
import { PrivateRoute } from 'src/components/PrivateRoute/PrivateRoute';
import { ScrollLayout } from '@components/ScrollLayout/ScrollLayout';
import { CoreLayout } from 'src/components/CoreLayout/CoreLayout';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateMomentUtils from '@date-io/moment';
import { NotificationsLine } from 'src/components/NotificationsLine/NotificationsLine';
import { ErrorBoundary } from 'src/components/ErrorBoundary/ErrorBoundary';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { PUBLIC_ROUTES } from 'src/constants/routes';
// import { Assesment } from './pages/Home/Assesment/Assesment';
import { store } from './store';

import './App.css';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY);

export const history = createBrowserHistory();

const Account = React.lazy(() => import('./pages/Account/Account'));
const Login = React.lazy(() => import('./pages/Login/Login'));
const CreateAccount = React.lazy(() => import('./pages/CreateAccount/CreateAccount'));
const EmailConfirm = React.lazy(() => import('./pages/EmailConfirm/EmailConfirm'));
const ResetPassword = React.lazy(() => import('./pages/ResetPassword/ResetPassword'));
const CreateNewPassword = React.lazy(() => import('./pages/ResetPassword/CreateNewPassword/CreateNewPassword'));
const PartnerShip = React.lazy(() => import('./pages/PartnerShip/PartnerShip'));
const EulerHermes = React.lazy(() => import('./pages/EulerHermes/EulerHermes'));
const NotFound = React.lazy(() => import('./pages/NotFound/NotFound'));
const PrivacyPolicy = React.lazy(() => import('./pages/PrivacyPolicy/PrivacyPolicy'));
const TermsConditions = React.lazy(() => import('./pages/TermsConditions/TermsConditions'));

const DiscrepancyReport = React.lazy(() => import('./pages/DiscrepancyReport/DiscrepancyReport'));
const Xero = React.lazy(() => import('./pages/Xero/Xero'));
const HomePage = React.lazy(() => import('./pages/HomePage/HomePage'));
const AboutUsPage = React.lazy(() => import('./pages/AboutUsPage/AboutUsPage'));
const FAQPage = React.lazy(() => import('src/pages/FAQPage/FAQPage'));
const AssessmentPage = React.lazy(() => import('src/pages/AssessmentPage/AssessmentPage'));
const CalculatorPage = React.lazy(() => import('src/pages/CalculatorPage/CalculatorPage'));
const AngryAtYourCustomersPage = React.lazy(() =>
  import('src/pages/AngryAtYourCustomersPage/AngryAtYourCustomersPage')
);
const PricingPage = React.lazy(() => import('src/pages/PricingPage/PricingPage'));
const RegisterWizardPasswordConfirm = React.lazy(() =>
  import('src/pages/RegisterWizardPasswordConfirm/RegisterWizardPasswordConfirm')
);
const CalendarAuthCallback = React.lazy(() => import('src/pages/CalendarAuthCallback/CalendarAuthCallback'));

export const App = () => {
  return (
    <ErrorBoundary>
      <CoreLayout>
        <Provider store={store}>
          <Router history={history}>
            <Elements stripe={stripePromise}>
              <ScrollLayout>
                <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
                  <ThemeProvider theme={theme}>
                    <MuiPickersUtilsProvider utils={DateMomentUtils}>
                      <CssBaseline />
                      <NotificationsLine />
                      <Suspense fallback={<CoreLoader />}>
                        <Switch>
                          <Route path={PUBLIC_ROUTES.HOME} component={Home} exact />
                          <Route path={PUBLIC_ROUTES.LOGIN} component={Login} exact />
                          <Route path={PUBLIC_ROUTES.REGISTER} component={CreateAccount} exact />
                          <Route path={PUBLIC_ROUTES.REGISTER_CONFIRMATION} component={EmailConfirm} exact />
                          <Route
                            path={PUBLIC_ROUTES.REGISTER_WIZARD_CONFIRMATION}
                            component={RegisterWizardPasswordConfirm}
                            exact
                          />
                          <Route path={PUBLIC_ROUTES.PASSWORD_RESET} component={ResetPassword} exact />
                          <Route path={PUBLIC_ROUTES.PASSWORD_RESET_VERIFY} component={CreateNewPassword} exact />
                          <Route path={PUBLIC_ROUTES.PARTNERSHIP} component={PartnerShip} exact />
                          <Route path={PUBLIC_ROUTES.EULER_HERMES} component={EulerHermes} exact />
                          <Route path={PUBLIC_ROUTES.DISCREPANCY_REPORT} component={DiscrepancyReport} exact />
                          <Route path={PUBLIC_ROUTES.XERO} component={Xero} />
                          <Route path={PUBLIC_ROUTES.HOME_PAGE} component={HomePage} />
                          {/* <Route path="/assessment" component={Assesment} exact /> */}
                          <Route path={PUBLIC_ROUTES.PRIVACY_POLICY} component={PrivacyPolicy} exact />
                          <Route path={PUBLIC_ROUTES.TERMS_AND_CONDITIONS} component={TermsConditions} exact />
                          <Route path={PUBLIC_ROUTES.ABOUT} component={AboutUsPage} exact />
                          <Route path={PUBLIC_ROUTES.FAQ} component={FAQPage} exact />
                          <Route path={PUBLIC_ROUTES.ASSESSMENT} component={AssessmentPage} exact />
                          <Route path={PUBLIC_ROUTES.CALCULATOR} component={CalculatorPage} exact />
                          <Route
                            path={PUBLIC_ROUTES.ANGRY_AT_YOUR_CUSTOMERS}
                            component={AngryAtYourCustomersPage}
                            exact
                          />
                          <Route path={PUBLIC_ROUTES.PRICING} component={PricingPage} exact />
                          <Route path={PUBLIC_ROUTES.CALENDAR_AUTH_CALLBACK} component={CalendarAuthCallback} exact />

                          <PrivateRoute path={PUBLIC_ROUTES.ACCOUNT} component={Account} />

                          <Route path={PUBLIC_ROUTES.NOT_FOUND} component={NotFound} exact />
                          <Redirect to={PUBLIC_ROUTES.HOME} />
                        </Switch>
                      </Suspense>
                    </MuiPickersUtilsProvider>
                  </ThemeProvider>
                </GoogleOAuthProvider>
              </ScrollLayout>
            </Elements>
          </Router>
        </Provider>
      </CoreLayout>
    </ErrorBoundary>
  );
};
